import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

export default function PreferencesStep({
  hubs = [],
  prefs,
  setPrefs,
  onPrev,
  onNext,
}) {
  const [local, setLocal] = useState(() => ({
    hasHub: prefs?.hasHub ?? null,                 // "yes" | "no" | null
    existingHubId: prefs?.existingHubId ?? "",
    onlyCompatibleWithExisting: prefs?.onlyCompatibleWithExisting ?? false,
    hubPolicy: prefs?.hubPolicy ?? "hub-ok",       // "no-hub-only" | "hub-ok"
  }));

  useEffect(() => {
    setPrefs?.(local);
  }, [local, setPrefs]);

  const canProceed = useMemo(() => {
    if (local.hasHub === "yes" && !local.existingHubId) return false;
    if (local.hasHub === null) return false;
    if (!local.hubPolicy) return false;
    return true;
  }, [local]);

  const hubOptions = useMemo(
    () =>
      hubs
        .map((h) => ({ id: h.id, name: h.name }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [hubs]
  );

  return (
    <motion.div
      className="bg-white rounded-2xl border border-gray-200 p-5"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <h2 className="text-2xl font-semibold mb-4">Preferenciák</h2>

      {/* 1) Van már központod / vezérlőd otthon? */}
      <section className="mb-6">
        <div className="font-medium mb-2">Van már központod/vezérlőd otthon?</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="hasHub"
              value="no"
              checked={local.hasHub === "no"}
              onChange={() => setLocal((s) => ({ ...s, hasHub: "no", existingHubId: "" }))}
            />
            Nincs, most kezdem
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="hasHub"
              value="yes"
              checked={local.hasHub === "yes"}
              onChange={() => setLocal((s) => ({ ...s, hasHub: "yes" }))}
            />
            Igen, van már
          </label>
        </div>

        {local.hasHub === "yes" && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-sm text-gray-700">Válaszd ki a meglévő központot</label>
              <select
                className="mt-1 w-full border rounded px-3 py-2"
                value={local.existingHubId}
                onChange={(e) =>
                  setLocal((s) => ({ ...s, existingHubId: e.target.value }))
                }
              >
                <option value="">— Válassz —</option>
                {hubOptions.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!local.onlyCompatibleWithExisting}
                onChange={(e) =>
                  setLocal((s) => ({
                    ...s,
                    onlyCompatibleWithExisting: e.target.checked,
                  }))
                }
              />
              <span>Csak olyan termékeket mutass, amelyek kompatibilisek a meglévő eszközömmel</span>
            </label>
          </div>
        )}
      </section>

      {/* 2) HUB szükségesség preferencia */}
      <section className="mb-6">
        <div className="font-medium mb-2">
          HUB szükségesség
        </div>
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="hubPolicy"
              value="no-hub-only"
              checked={local.hubPolicy === "no-hub-only"}
              onChange={() => setLocal((s) => ({ ...s, hubPolicy: "no-hub-only" }))}
            />
            Csak olyan terméket szeretnék, amihez <strong>nem kell HUB</strong> (appal közvetlenül vezérelhető)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="hubPolicy"
              value="hub-ok"
              checked={local.hubPolicy === "hub-ok"}
              onChange={() => setLocal((s) => ({ ...s, hubPolicy: "hub-ok" }))}
            />
            Nem gond, ha kell hozzá HUB
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Tipp: sok Wi‑Fi‑s eszköz működik HUB nélkül is a gyártó alkalmazásával; Zigbee/Thread eszközökhöz általában kell központ.
        </p>
      </section>

      <div className="flex justify-between mt-6">
        <button className="border px-5 py-2 rounded hover:bg-gray-50" onClick={onPrev}>
          Vissza
        </button>
        <button
          className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={!canProceed}
          onClick={onNext}
        >
          Tovább
        </button>
      </div>
    </motion.div>
  );
}
